import React, { useState, useEffect } from 'react';
import { Formik, Form, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import merge from 'lodash.merge';

import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import RadioGroup from '@material-ui/core/RadioGroup';
import Radio from '@material-ui/core/Radio';
import Typography from '@material-ui/core/Typography';

import compose from 'helpers/compose';
import { scrollToTop } from 'helpers/helpers';
import Switch from 'components/FormFields/Switch';
import DatePicker from 'components/FormFields/FormDatePicker';
import FormTextField from 'components/FormFields/FormTextField';
import FormSelectField from 'components/FormFields/FormSelectField';
import Background from 'components/Layout/Background';
import withoutEnterSubmit from 'components/withoutEnterSubmit';

import { RootStore } from 'stores/rootStore';
import PlanningForStore from 'stores/workflowStores/finances/planningFor/planningForStore';
import { initialValues, Partner } from 'stores/workflowStores/finances/planningFor/model';
import { RentModalsStore } from 'stores/workflowStores/finances/rentModalsStore';

const FormWithoutEnterSubmit = withoutEnterSubmit(Form);

interface Props {
  classes: Record<string, string>;
  rentModalsStore: RentModalsStore;
  rootStore: RootStore;
  accountId: string;
  detailMode?: boolean;
}

const PlanningFor: React.FC<Props> = ({ accountId, rootStore, rentModalsStore, detailMode }) => {
  const [store] = useState(new PlanningForStore(rootStore, rentModalsStore, accountId, detailMode));
  const { t } = useTranslation();

  useEffect(() => {
    scrollToTop('.MuiDialogContent-root');
    store.fetchPartner();
  }, [store]);

  const handleSubmit = async (values: Partner, formikHelpers: FormikHelpers<Partner>) => {
    rentModalsStore.setRentSubmitting(true);
    store.planningFor === 'meAndPartner'
      ? await store.submitPartner(values, formikHelpers)
      : await store.deletePartner(values, formikHelpers);
    rentModalsStore.setRentSubmitting(false);
  };

  return (
    store.data && (
      <Background inside>
        <Typography variant="h2">{t('finQuestionnary_forWho_title')}</Typography>

        <Grid container>
          <Grid item xs={6}>
            <Typography variant="body2">{t('finQuestionnary_forWho_info')}</Typography>
            <FormControl component="fieldset">
              <RadioGroup
                value={store.planningFor}
                name="planningFor"
                onChange={(e) => store.setPlanningFor(e.target.value as 'onlyMe' | 'meAndPartner')}
              >
                <FormControlLabel
                  value="onlyMe"
                  control={<Radio />}
                  label={t('finQuestionnary_forWho_onlyMe')}
                />
                <FormControlLabel
                  value="meAndPartner"
                  control={<Radio />}
                  label={t('finQuestionnary_forWho_meAndPartner')}
                />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <Formik initialValues={merge({}, initialValues, store.data)} onSubmit={handleSubmit}>
              <FormWithoutEnterSubmit id={rentModalsStore.rentFormKey}>
                {store.planningFor === 'meAndPartner' && (
                  <Grid container>
                    <Grid item xs={12}>
                      <FormTextField
                        name="firstname"
                        label={t('finQuestionnary_forWho_partnersFirstName')}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormTextField name="lastname" label={t('general_surname')} />
                    </Grid>
                    <Grid item xs={12}>
                      <FormSelectField
                        name="gender"
                        label={t('general_gender')}
                        options={store.data.options?.gender.map((gender) => ({
                          value: gender,
                          label: t(`gender_${gender}`),
                        }))}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <DatePicker
                        name="birthDate"
                        label={t('general_birthDate')}
                        InputLabelProps={{ shrink: true }}
                        disableFuture
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Switch
                        label={t('finQuestionnary_forWho_isInRetirement')}
                        name="retired"
                        tooltipContent={t('tooltip_planningFor_partnerRetired')}
                      />
                    </Grid>
                  </Grid>
                )}
              </FormWithoutEnterSubmit>
            </Formik>
          </Grid>
        </Grid>
      </Background>
    )
  );
};

export default compose(inject('rootStore'), observer)(PlanningFor);
